import {
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';
import { BarChart3, Calendar, IndianRupee, TrendingUp } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';

import { ToolLayout } from '@components/layouts/ToolLayout';

import { Button } from '@ui/Button';
import { LocalizedCurrency } from '@ui/LocalizedNumber';
import NumberInputWithSlider from '@ui/NumberInputWithSlider';
import ResultCard from '@ui/ResultCard';
import { ResultSummaryCard } from '@ui/ResultSummaryCard';
import { Formula } from '@ui/Typography';
import { WhatsAppCTA } from '@ui/WhatsAppCTA';

import { COLORS } from '@constants/colors';
import { CONTACT_INFO } from '@constants/contact';

import { formatIndianNumberShort } from '@utils/numberFormatter';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface CAGRResult {
  cagr: number;
  totalReturn: number;
  totalReturnPercentage: number;
  absoluteGain: number;
  annualizedReturn: number;
}

export const CAGRCalculator = () => {
  // Input states
  const [initialInvestment, setInitialInvestment] = useState<number | null>(
    100000
  );
  const [currentValue, setCurrentValue] = useState<number | null>(200000);
  const [investmentYears, setInvestmentYears] = useState<number | null>(5);
  const [investmentMonths, setInvestmentMonths] = useState<number | null>(0);

  // Result state
  const [result, setResult] = useState<CAGRResult | null>(null);

  // Calculate CAGR
  const calculateCAGR = useCallback(() => {
    const initialInv = initialInvestment || 0;
    const currentVal = currentValue || 0;
    const years = investmentYears || 0;
    const months = investmentMonths || 0;

    if (initialInv <= 0 || currentVal <= 0 || (years === 0 && months === 0)) {
      setResult(null);
      return;
    }

    // Convert total time to years
    const totalYears = years + months / 12;

    if (totalYears <= 0) {
      setResult(null);
      return;
    }

    // CAGR formula: ((Current Value / Initial Investment)^(1/years)) - 1
    const cagr = (Math.pow(currentVal / initialInv, 1 / totalYears) - 1) * 100;

    // Total return calculations
    const absoluteGain = currentVal - initialInv;
    const totalReturnPercentage =
      ((currentVal - initialInv) / initialInv) * 100;
    const annualizedReturn = cagr;

    setResult({
      cagr: cagr,
      totalReturn: currentVal,
      totalReturnPercentage: totalReturnPercentage,
      absoluteGain: absoluteGain,
      annualizedReturn: annualizedReturn,
    });
  }, [initialInvestment, currentValue, investmentYears, investmentMonths]);

  // Auto-calculate when inputs change
  useEffect(() => {
    calculateCAGR();
  }, [
    initialInvestment,
    currentValue,
    investmentYears,
    investmentMonths,
    calculateCAGR,
  ]);

  // Performance rating based on CAGR
  const getPerformanceRating = (cagr: number) => {
    if (cagr >= 15) return { rating: 'Excellent', color: COLORS.sage };
    if (cagr >= 12) return { rating: 'Very Good', color: COLORS.accent };
    if (cagr >= 8) return { rating: 'Good', color: COLORS.gold };
    if (cagr >= 5) return { rating: 'Average', color: '#f59e0b' };
    if (cagr > 0) return { rating: 'Below Average', color: '#ef4444' };
    return { rating: 'Loss', color: '#dc2626' };
  };

  const performance = result ? getPerformanceRating(result.cagr) : null;

  // Generate chart data for investment growth visualization
  const generateChartData = () => {
    const initialInv = initialInvestment || 0;
    const currentVal = currentValue || 0;
    const years = investmentYears || 0;
    const months = investmentMonths || 0;

    if (initialInv <= 0 || currentVal <= 0 || (years === 0 && months === 0)) {
      return { labels: [], datasets: [] };
    }

    const totalYears = years + months / 12;
    const cagr = result?.cagr || 0;
    const annualRate = cagr / 100;
    const isNegativeGrowth = cagr < 0;

    const labels = [];
    const investmentData = [];
    const growthData = [];

    // Add starting point
    labels.push('Start');
    investmentData.push(initialInv);
    growthData.push(initialInv);

    // Generate data points for each year
    const dataPoints = Math.min(Math.max(Math.ceil(totalYears), 5), 30); // Show at least 5 points, max 30
    const interval = totalYears / (dataPoints - 1);

    for (let i = 1; i < dataPoints; i++) {
      const yearPoint = i * interval;
      const valueAtPoint = initialInv * Math.pow(1 + annualRate, yearPoint);

      labels.push(
        yearPoint < 1
          ? `${Math.round(yearPoint * 12)}m`
          : `${yearPoint.toFixed(1)}y`
      );
      investmentData.push(initialInv);
      growthData.push(Math.max(0, valueAtPoint)); // Ensure we don't show negative values
    }

    // Ensure the final point matches the actual current value
    if (growthData.length > 1) {
      growthData[growthData.length - 1] = currentVal;
    }

    return {
      labels,
      datasets: [
        {
          label: 'Initial investment',
          data: investmentData,
          borderColor: COLORS.accent,
          backgroundColor: `${COLORS.accent}20`,
          fill: true,
          tension: 0,
          pointBackgroundColor: COLORS.accent,
          pointBorderColor: COLORS.accent,
          pointRadius: 3,
          pointHoverRadius: 5,
        },
        {
          label: isNegativeGrowth ? 'Investment decline' : 'Investment growth',
          data: growthData,
          borderColor: isNegativeGrowth ? '#ef4444' : COLORS.sage,
          backgroundColor: isNegativeGrowth ? '#ef444420' : `${COLORS.sage}20`,
          fill: false,
          tension: 0.4,
          pointBackgroundColor: isNegativeGrowth ? '#ef4444' : COLORS.sage,
          pointBorderColor: isNegativeGrowth ? '#ef4444' : COLORS.sage,
          pointRadius: 4,
          pointHoverRadius: 6,
        },
      ],
    };
  };

  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          usePointStyle: true,
          padding: 20,
          font: {
            size: 12,
            weight: 'bold' as const,
          },
        },
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: COLORS.sage,
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
        callbacks: {
          label: function (context: any) {
            const value = `₹${context.parsed.y.toLocaleString('en-IN')}`;
            return `${context.dataset.label}: ${value}`;
          },
        },
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Time Period',
          font: {
            size: 12,
            weight: 'bold' as const,
          },
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
        },
      },
      y: {
        title: {
          display: true,
          text: 'Value (₹)',
          font: {
            size: 12,
            weight: 'bold' as const,
          },
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
        },
        ticks: {
          callback: function (value: any) {
            return '₹' + value.toLocaleString('en-IN');
          },
        },
      },
    },
    interaction: {
      intersect: false,
      mode: 'index' as const,
    },
  };

  return (
    <ToolLayout
      title={`CAGR calculator | ${CONTACT_INFO.company.name}`}
      heroTitle="CAGR calculator"
      description="Calculate your Compound Annual Growth Rate (CAGR) to measure investment performance over time"
      canonicalUrl="https://niveshright.com/tools/cagr-calculator"
      backgroundColor={COLORS.mist}
      heroStyle="gradient"
      heroIcon={
        <div
          className="w-16 h-16 rounded-2xl flex items-center justify-center"
          style={{ backgroundColor: `${COLORS.gold}20` }}
        >
          <BarChart3 size={32} style={{ color: COLORS.gold }} />
        </div>
      }
      showBreadcrumb={true}
      keywords="CAGR calculator, compound annual growth rate, investment returns, annualized returns, investment performance, portfolio analysis"
      cta={
        <WhatsAppCTA
          title="Need investment advice?"
          description={
            result ? (
              <>
                Your investment generated a CAGR of{' '}
                <strong>{result.cagr.toFixed(2)}%</strong>. Get expert advice on
                improving your investment strategy.
              </>
            ) : (
              'Get personalized guidance on improving your investment returns and portfolio strategy.'
            )
          }
          phoneNumber={CONTACT_INFO.phone.whatsapp}
          message={
            result
              ? `Hi ${CONTACT_INFO.contact_person.short_name}! I used your CAGR calculator with these details:

Initial investment: ₹${(initialInvestment || 0).toLocaleString('en-IN')}
Current value: ₹${(currentValue || 0).toLocaleString('en-IN')}
Investment period: ${investmentYears || 0}y ${investmentMonths || 0}m

CAGR RESULTS:
• CAGR: ${result.cagr.toFixed(2)}%
• Total return: ₹${result.totalReturn.toLocaleString('en-IN')}
• Absolute gain: ₹${result.absoluteGain.toLocaleString('en-IN')}
• Total return %: ${result.totalReturnPercentage.toFixed(2)}%

I'd like personalized advice on improving my investment performance and future strategy. Can you help me analyze my portfolio and suggest better investment options?`
              : `Hi ${CONTACT_INFO.contact_person.short_name}, I used your CAGR calculator and would like to discuss my investment performance and future strategy.`
          }
          buttonText="Get expert advice"
          backgroundColor={COLORS.pearl}
          buttonColor={COLORS.gold}
        />
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        {/* Input Section */}
        <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
          <h2
            className="text-2xl font-bold mb-8"
            style={{ color: COLORS.primary }}
          >
            Investment details
          </h2>

          <NumberInputWithSlider
            label="Initial investment amount"
            placeholder="0"
            value={initialInvestment}
            onChange={setInitialInvestment}
            min={1000}
            max={100000000}
            step={1000}
            prefix="₹"
            showTooltip={true}
            minLabel={`₹${formatIndianNumberShort(1000, false)}`}
            maxLabel={`₹${formatIndianNumberShort(100000000, false)}`}
            className="mb-8"
          />

          <NumberInputWithSlider
            label="Current value"
            placeholder="0"
            value={currentValue}
            onChange={setCurrentValue}
            min={100}
            max={1000000000}
            step={1000}
            prefix="₹"
            showTooltip={true}
            minLabel={`₹${formatIndianNumberShort(100, false)}`}
            maxLabel={`₹${formatIndianNumberShort(1000000000, false)}`}
            className="mb-8"
          />

          <div className="grid grid-cols-2 gap-4">
            <NumberInputWithSlider
              label="Investment years"
              placeholder="0"
              value={investmentYears}
              onChange={value =>
                setInvestmentYears(value ? Math.round(value) : value)
              }
              min={0}
              max={50}
              step={1}
              minLabel="0 years"
              maxLabel="50 years"
              className="mb-8"
            />

            <NumberInputWithSlider
              label="Additional months"
              placeholder="0"
              value={investmentMonths}
              onChange={value =>
                setInvestmentMonths(value ? Math.round(value) : value)
              }
              min={0}
              max={11}
              step={1}
              minLabel="0 months"
              maxLabel="11 months"
              className="mb-8"
            />
          </div>

          {/* Quick Examples */}
          <div
            className="mt-8 p-6 rounded-xl hidden md:block"
            style={{ backgroundColor: `${COLORS.mist}50` }}
          >
            <h3
              className="font-semibold mb-4"
              style={{ color: COLORS.primary }}
            >
              Quick examples:
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 text-sm">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setInitialInvestment(100000);
                  setCurrentValue(200000);
                  setInvestmentYears(3);
                  setInvestmentMonths(0);
                }}
                className="justify-start text-left"
              >
                ₹1L → ₹2L (3y)
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setInitialInvestment(100000);
                  setCurrentValue(200000);
                  setInvestmentYears(5);
                  setInvestmentMonths(0);
                }}
                className="justify-start text-left"
              >
                ₹1L → ₹2L (5y)
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setInitialInvestment(100000);
                  setCurrentValue(200000);
                  setInvestmentYears(10);
                  setInvestmentMonths(0);
                }}
                className="justify-start text-left"
              >
                ₹1L → ₹2L (10y)
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setInitialInvestment(100000);
                  setCurrentValue(500000);
                  setInvestmentYears(15);
                  setInvestmentMonths(0);
                }}
                className="justify-start text-left"
              >
                ₹1L → ₹5L (15y)
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setInitialInvestment(100000);
                  setCurrentValue(1000000);
                  setInvestmentYears(20);
                  setInvestmentMonths(0);
                }}
                className="justify-start text-left"
              >
                ₹1L → ₹10L (20y)
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setInitialInvestment(100000);
                  setCurrentValue(2000000);
                  setInvestmentYears(25);
                  setInvestmentMonths(0);
                }}
                className="justify-start text-left"
              >
                ₹1L → ₹20L (25y)
              </Button>
            </div>
          </div>
        </div>

        {/* Results Section */}
        <div className="space-y-6">
          {result ? (
            <>
              {/* Main CAGR Result */}
              <ResultSummaryCard
                title="Compound Annual Growth Rate (CAGR)"
                value={`${result.cagr.toFixed(2)}%`}
                subtitle={
                  performance ? `Performance: ${performance.rating}` : ''
                }
                icon={TrendingUp}
                iconColor={performance?.color || COLORS.accent}
                valueColor={performance?.color || COLORS.accent}
              />

              {/* Detailed Results */}
              <div className="grid grid-cols-1 gap-4">
                <ResultCard
                  title="Total return"
                  value={<LocalizedCurrency value={result.totalReturn} />}
                  subtitle="Current investment value"
                  icon={<IndianRupee size={20} />}
                />

                <ResultCard
                  title="Absolute gain"
                  value={<LocalizedCurrency value={result.absoluteGain} />}
                  subtitle={`${result.totalReturnPercentage.toFixed(2)}% total return`}
                  icon={<TrendingUp size={20} />}
                  color={result.absoluteGain > 0 ? COLORS.sage : '#ef4444'}
                />

                <ResultCard
                  title="Investment period"
                  value={`${investmentYears || 0}y ${investmentMonths || 0}m`}
                  subtitle={`${((investmentYears || 0) + (investmentMonths || 0) / 12).toFixed(1)} years total`}
                  icon={<Calendar size={20} />}
                />
              </div>

              {/* Performance Analysis */}
              <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                <h3
                  className="text-lg font-semibold mb-4"
                  style={{ color: COLORS.primary }}
                >
                  Performance analysis
                </h3>

                <div className="space-y-4">
                  <div
                    className="flex justify-between items-center p-3 rounded-lg"
                    style={{ backgroundColor: `${performance?.color}15` }}
                  >
                    <span className="font-medium">Performance rating</span>
                    <span
                      className="font-bold"
                      style={{ color: performance?.color }}
                    >
                      {performance?.rating}
                    </span>
                  </div>

                  <div className="text-sm text-gray-600 space-y-2">
                    <p>
                      <strong>Benchmark comparison:</strong>
                    </p>
                    <ul className="space-y-1 ml-4 list-disc">
                      <li>Fixed deposits: ~6-7% annually</li>
                      <li>Equity mutual funds: ~12-15% annually</li>
                      <li>Nifty 50 (10-year): ~13-14% annually</li>
                      <li>Gold (10-year): ~8-10% annually</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Growth Chart */}
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
                <div className="flex items-center mb-6">
                  <BarChart3
                    size={24}
                    style={{ color: COLORS.gold }}
                    className="mr-3"
                  />
                  <h3
                    className="text-lg font-semibold"
                    style={{ color: COLORS.primary }}
                  >
                    Investment growth visualization
                  </h3>
                </div>

                <div className="h-80">
                  <Line data={generateChartData()} options={chartOptions} />
                </div>

                <div className="mt-6 grid grid-cols-2 gap-4 text-center">
                  <div className="p-3 bg-blue-50 rounded-xl">
                    <p className="text-sm font-medium text-gray-600">
                      Initial investment
                    </p>
                    <p
                      className="text-lg font-bold"
                      style={{ color: COLORS.accent }}
                    >
                      <LocalizedCurrency value={initialInvestment || 0} />
                    </p>
                  </div>
                  <div
                    className={`p-3 rounded-xl ${result.cagr < 0 ? 'bg-red-50' : 'bg-green-50'}`}
                  >
                    <p className="text-sm font-medium text-gray-600">
                      Current value
                    </p>
                    <p
                      className="text-lg font-bold"
                      style={{
                        color: result.cagr < 0 ? '#ef4444' : COLORS.sage,
                      }}
                    >
                      <LocalizedCurrency value={currentValue || 0} />
                    </p>
                  </div>
                </div>

                <div
                  className={`mt-4 p-4 rounded-xl text-center ${
                    result.cagr < 0
                      ? 'bg-gradient-to-r from-red-50 to-orange-50'
                      : 'bg-gradient-to-r from-green-50 to-blue-50'
                  }`}
                >
                  <p className="text-sm font-medium text-gray-600 mb-1">
                    CAGR {result.cagr < 0 ? 'decline' : 'achieved'}
                  </p>
                  <p
                    className="text-2xl font-bold"
                    style={{ color: result.cagr < 0 ? '#ef4444' : COLORS.sage }}
                  >
                    {result.cagr.toFixed(2)}%
                  </p>
                  <p className="text-sm text-gray-500">
                    {result.totalReturnPercentage.toFixed(1)}% total{' '}
                    {result.cagr < 0 ? 'decline' : 'growth'} •{' '}
                    {(result.totalReturn / (initialInvestment || 1)).toFixed(2)}
                    {'x'} multiplier
                  </p>
                </div>
              </div>
            </>
          ) : (
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 text-center">
              <BarChart3
                size={48}
                className="mx-auto mb-4"
                style={{ color: COLORS.mist }}
              />
              <p className="text-gray-500">
                Enter your investment details to calculate CAGR
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Educational Content */}
      <div className="mt-16 max-w-5xl mx-auto px-6">
        <article className="prose prose-lg md:prose-xl max-w-none">
          <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-6 md:mb-8">
            Understanding CAGR (Compound Annual Growth Rate)
          </h2>

          <p className="text-lg text-gray-700 leading-relaxed mb-6 md:mb-8">
            <strong>CAGR (Compound Annual Growth Rate)</strong> is the rate at
            which an investment would have grown if it had grown at a steady
            rate. It's one of the most accurate ways to calculate and compare
            returns for investments that can rise and fall in value over time.
          </p>

          <h3 className="text-xl md:text-2xl font-semibold text-gray-900 mt-8 md:mt-10 mb-4 md:mb-6">
            How CAGR is calculated
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6 md:mb-8">
            CAGR uses the following formula to smooth out the effects of
            volatility:
          </p>

          <Formula title="CAGR Formula">
            {
              'CAGR = \\left(\\frac{\\text{Current value}}{\\text{Initial investment}}\\right)^{\\frac{1}{\\text{Years}}} - 1'
            }
          </Formula>

          {result && (
            <>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                <strong>Your calculation:</strong>
              </p>
              <Formula>
                {`CAGR = \\left(\\frac{${currentValue || 0}}{${initialInvestment || 0}}\\right)^{\\frac{1}{${((investmentYears || 0) + (investmentMonths || 0) / 12).toFixed(1)}}} - 1 = ${result.cagr.toFixed(2)}\\%`}
              </Formula>
            </>
          )}

          <h3 className="text-xl md:text-2xl font-semibold text-gray-900 mt-8 md:mt-10 mb-4 md:mb-6">
            Why use CAGR?
          </h3>
          <div className="space-y-4 md:space-y-5 mb-8 md:mb-10">
            <ul className="text-lg text-gray-700 list-disc ml-4">
              <li>
                CAGR eliminates the noise of year-to-year fluctuations, giving
                you a clear picture of average annual growth.
              </li>
              <li>
                You can use it to compare different investments on an
                apples-to-apples basis, regardless of their investment periods.
              </li>
              <li>
                Unlike simple returns, CAGR considers the compounding effect of
                reinvested gains.
              </li>
            </ul>
          </div>

          <h3 className="text-xl md:text-2xl font-semibold text-gray-900 mt-8 md:mt-10 mb-4 md:mb-6">
            CAGR vs other return metrics
          </h3>

          <h4 className="text-lg md:text-xl font-semibold text-gray-900 mt-6 md:mt-8 mb-3 md:mb-4">
            Absolute return
          </h4>
          <ul className="text-lg text-gray-700 mb-4 md:mb-6 list-disc ml-4">
            <li>Simple percentage gain or loss without considering time.</li>
            <li>
              Example: ₹1L becomes ₹2L = 100% absolute return (but doesn't tell
              you if it took 2 years or 10 years
            </li>
          </ul>

          <h4 className="text-lg md:text-xl font-semibold text-gray-900 mt-6 md:mt-8 mb-3 md:mb-4">
            Average return
          </h4>
          <ul className="text-lg text-gray-700 mb-4 md:mb-6 list-disc ml-4">
            <li>Arithmetic mean of yearly returns.</li>
            <li>Can be misleading due to volatility.</li>
            <li>
              Example: +50% year followed by a -50% year averages to 0%, but you
              actually lose 25%.
            </li>
          </ul>

          <h4 className="text-lg md:text-xl font-semibold text-gray-900 mt-6 md:mt-8 mb-3 md:mb-4">
            CAGR
          </h4>
          <ul className="text-lg text-gray-700 mb-4 md:mb-6 list-disc ml-4">
            <li>Geometric mean that considers compounding and time.</li>
            <li>
              Advantage: Shows the actual rate at which your money grew
              annually, accounting for all ups and downs.
            </li>
            <li>
              Example: A 15% CAGR over 10 years means your investment grew from
              1000 to 4045.
            </li>
          </ul>

          <h3 className="text-xl md:text-2xl font-semibold text-gray-900 mt-8 md:mt-10 mb-4 md:mb-6">
            Benchmark comparisons
          </h3>
          <div className="space-y-4 md:space-y-5 mb-8 md:mb-10">
            <p className="text-lg text-gray-700">
              <strong>Fixed Deposits (FDs):</strong> ~6-7% CAGR annually (safe
              but low returns)
            </p>
            <p className="text-lg text-gray-700">
              <strong>Equity mutual funds:</strong> ~12-15% CAGR annually
              (higher risk, higher returns)
            </p>
            <p className="text-lg text-gray-700">
              <strong>Nifty 50 (10-year):</strong> ~13-14% CAGR annually (market
              benchmark)
            </p>
            <p className="text-lg text-gray-700">
              <strong>Gold (10-year):</strong> ~8-10% CAGR annually (inflation
              hedge)
            </p>
          </div>

          <h3 className="text-xl md:text-2xl font-semibold text-gray-900 mt-8 md:mt-10 mb-4 md:mb-6">
            Important limitations
          </h3>
          <ul className="mb-8 md:mb-10 list-disc ml-4">
            <li className="text-lg text-gray-700">
              It does't show volatility. Meaning a steady 10% CAGR and a
              volatile investment with +30%, -10%, +20%, -5% annual returns for
              respective years show similar CAGR but very different risk
              profiles.
            </li>
            <li className="text-lg text-gray-700">
              CAGR smooths out fluctuations, which may not reflect the actual
              investment experience.
            </li>
            <li className="text-lg text-gray-700">
              Historical CAGR doesn't guarantee future returns. Market
              conditions change.
            </li>
          </ul>

          <h3 className="text-xl md:text-2xl font-semibold text-gray-900 mt-8 md:mt-10 mb-4 md:mb-6">
            When to use CAGR
          </h3>
          <p className="text-lg text-gray-700 leading-relaxed mb-6 md:mb-8">
            CAGR is perfect for evaluating long-term investment performance,
            comparing different investment options, and setting realistic return
            expectations. Use it to assess mutual funds, stocks, real estate, or
            any investment held for multiple years.
          </p>
        </article>
      </div>
    </ToolLayout>
  );
};
